import type {
  ChecklistItemResponse,
  ChecklistItemStatus,
  ChecklistResponse,
  ChecklistSectionResponse,
  ChecklistStatusRollup,
  RoomStatus,
} from '../api/types'

/*
  The request list's rules as the screen needs them (ADR 0006, extended by ADR 0007). The API
  decides again on every click; everything here only keeps the screen from offering a step
  that would be refused, and from showing 119 requests as one undifferentiated column.
*/

export const CHECKLIST_STATUSES: ReadonlyArray<ChecklistItemStatus> = [
  'Open',
  'InProgress',
  'Submitted',
  'NeedsRevision',
  'Completed',
  'NotApplicable',
]

/** What a status is called on screen. */
export function checklistStatusLabel(status: string): string {
  switch (status) {
    case 'InProgress':
      return 'In progress'
    case 'Submitted':
      return 'Submitted, awaiting sign-off'
    case 'NeedsRevision':
      return 'Sent back'
    case 'Completed':
      return 'Completed'
    case 'NotApplicable':
      return 'Not applicable'
    default:
      return 'Open'
  }
}

/** The workbook's cover-sheet bucket for a state: what the deal team's own file calls it. */
export function checklistRollup(status: string): ChecklistStatusRollup {
  switch (status) {
    case 'Open':
      return 'Not started'
    case 'Completed':
      return 'Done'
    case 'NotApplicable':
      return 'N/A'
    default:
      return 'WIP'
  }
}

/** A terminal item takes no new file or link (ADR 0007 s5). */
export const isTerminalStatus = (status: string): boolean =>
  status === 'Completed' || status === 'NotApplicable'

/** Sending back and "not applicable" are explained, always. */
export const statusNeedsReason = (status: string): boolean =>
  status === 'NeedsRevision' || status === 'NotApplicable'

/** ADR 0007 s4: the states each target can be reached from. */
const REACHABLE_FROM: Record<ChecklistItemStatus, ReadonlyArray<ChecklistItemStatus>> = {
  InProgress: ['Open', 'NeedsRevision', 'NotApplicable'],
  Submitted: ['Open', 'InProgress', 'NeedsRevision'],
  NeedsRevision: ['Submitted'],
  Completed: ['Open', 'InProgress', 'Submitted', 'NeedsRevision'],
  NotApplicable: ['Open', 'InProgress', 'Submitted', 'NeedsRevision'],
  Open: ['InProgress', 'Submitted', 'NeedsRevision', 'Completed', 'NotApplicable'],
}

export interface ChecklistMove {
  to: ChecklistItemStatus
  /** The button's words. */
  label: string
  needsReason: boolean
}

const MOVE_LABELS: Record<ChecklistItemStatus, string> = {
  InProgress: 'Start',
  Submitted: 'Mark done',
  NeedsRevision: 'Send back',
  Completed: 'Sign off',
  NotApplicable: 'Not applicable',
  Open: 'Reopen',
}

/**
 * The moves to offer for one item, in the order a person would reach for them.
 *
 * The responder's moves (InProgress, Submitted, NotApplicable) need `canSubmit` and an OPEN
 * room. The owner's (NeedsRevision, Completed, reopening, NotApplicable, and un-marking a
 * NotApplicable item straight back to InProgress) need `canManage` and an Open or Frozen room,
 * so a list can still be closed out after the freeze. Nothing moves in a Draft or Archived room.
 */
export function checklistMoves(
  item: Pick<ChecklistItemResponse, 'status'>,
  checklist: Pick<ChecklistResponse, 'canManage' | 'canSubmit'>,
  roomStatus: RoomStatus,
): ChecklistMove[] {
  const roomOpen = roomStatus === 'Open'
  const roomLive = roomOpen || roomStatus === 'Frozen'
  const order: ChecklistItemStatus[] = [
    'InProgress',
    'Submitted',
    'Completed',
    'NeedsRevision',
    'NotApplicable',
    'Open',
  ]

  return order
    .filter((to) => REACHABLE_FROM[to].includes(item.status))
    .filter((to) => {
      const ownersMove =
        to === 'Completed' ||
        to === 'NeedsRevision' ||
        to === 'Open' ||
        (to === 'InProgress' && item.status === 'NotApplicable') ||
        (to === 'NotApplicable' && checklist.canManage)
      return ownersMove ? checklist.canManage && roomLive : checklist.canSubmit && roomOpen
    })
    .map((to) => ({
      to,
      label:
        to === 'InProgress' && item.status === 'NotApplicable' ? 'Back to work' : MOVE_LABELS[to],
      needsReason: statusNeedsReason(to),
    }))
}

/** Structure (sections, items, order, building from a template) is the owner's, in a Draft or Open room. */
export const canEditChecklist = (
  checklist: Pick<ChecklistResponse, 'canManage'>,
  roomStatus: RoomStatus,
): boolean => checklist.canManage && (roomStatus === 'Draft' || roomStatus === 'Open')

/**
 * The items a picker offers for one document or upload: every living item that is not
 * terminal, plus a terminal item the document already carries (so the link can be kept or
 * dropped, never added). The API refuses a new link under a Completed or NotApplicable item;
 * the owner reopens the item if a late file belongs there.
 */
export function offeredChecklistItems<T extends Pick<ChecklistItemResponse, 'id' | 'status'>>(
  items: ReadonlyArray<T>,
  selected: ReadonlyArray<string>,
): T[] {
  return items.filter((i) => !isTerminalStatus(i.status) || selected.includes(i.id))
}

/** What a chip, a picker row or a heading calls an item: its label when it has one. */
export const checklistItemName = (
  item: Pick<ChecklistItemResponse, 'docLabel' | 'title'>,
): string => (item.docLabel === null ? item.title : `${item.docLabel} ${item.title}`)

export const UNCATEGORIZED_SECTION = 'Uncategorized'
export const GENERAL_CATEGORY = 'General'

export interface ChecklistCategoryGroup<T> {
  category: string
  items: T[]
}

export interface ChecklistSectionGroup<T> {
  /** Null for the items that have no section: a list written before sections existed. */
  section: ChecklistSectionResponse | null
  name: string
  categories: ChecklistCategoryGroup<T>[]
  items: T[]
}

type Groupable = Pick<ChecklistItemResponse, 'sectionId' | 'category' | 'position'>

/**
 * The list as a person reads it: sections in their order, the items with no section last under
 * "Uncategorized", and inside each the categories in the order their first item appears (a
 * category has no order of its own, ADR 0007 s1), items by position. A section with no items is
 * kept, so an owner sees the one they have just made; "Uncategorized" appears only when it holds
 * something.
 */
export function groupChecklist<T extends Groupable>(
  sections: ReadonlyArray<ChecklistSectionResponse>,
  items: ReadonlyArray<T>,
): ChecklistSectionGroup<T>[] {
  const known = new Set(sections.map((s) => s.id))
  const bySection = (id: string | null) =>
    items
      .filter((i) =>
        id === null ? i.sectionId === null || !known.has(i.sectionId) : i.sectionId === id,
      )
      .slice()
      .sort((a, b) => a.position - b.position)

  const group = (
    section: ChecklistSectionResponse | null,
    name: string,
    own: T[],
  ): ChecklistSectionGroup<T> => {
    const categories: ChecklistCategoryGroup<T>[] = []
    for (const item of own) {
      const category = item.category ?? GENERAL_CATEGORY
      const existing = categories.find((c) => c.category === category)
      if (existing) existing.items.push(item)
      else categories.push({ category, items: [item] })
    }
    return { section, name, categories, items: own }
  }

  const groups = sections
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((s) => group(s, s.name, bySection(s.id)))
  const loose = bySection(null)
  if (loose.length > 0) groups.push(group(null, UNCATEGORIZED_SECTION, loose))
  return groups
}

/**
 * Whether an item answers a search typed into the picker: every word must appear in its label,
 * title, category or request. "2035", "census" and "form 5500" all find what they should.
 */
export function matchesChecklistSearch(
  item: Pick<ChecklistItemResponse, 'docLabel' | 'title' | 'category' | 'requestText'>,
  query: string,
): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return true
  const haystack = [item.docLabel ?? '', item.title, item.category ?? '', item.requestText ?? '']
    .join('\n')
    .toLowerCase()
  return words.every((w) => haystack.includes(w))
}

export interface ChecklistCounts {
  total: number
  notStarted: number
  wip: number
  done: number
  notApplicable: number
  awaitingSignOff: number
  sentBack: number
}

/** Counts in the workbook's four buckets, plus the two WIP states an owner and a responder act on. */
export function checklistCounts(
  items: ReadonlyArray<Pick<ChecklistItemResponse, 'status'>>,
): ChecklistCounts {
  const counts: ChecklistCounts = {
    total: items.length,
    notStarted: 0,
    wip: 0,
    done: 0,
    notApplicable: 0,
    awaitingSignOff: 0,
    sentBack: 0,
  }
  for (const item of items) {
    const bucket = checklistRollup(item.status)
    if (bucket === 'Not started') counts.notStarted++
    else if (bucket === 'WIP') counts.wip++
    else if (bucket === 'Done') counts.done++
    else counts.notApplicable++
    if (item.status === 'Submitted') counts.awaitingSignOff++
    if (item.status === 'NeedsRevision') counts.sentBack++
  }
  return counts
}

/** A summary line: "2 of 5 done, 1 not applicable, 1 awaiting sign-off, 1 sent back". */
export function checklistSummary(
  items: ReadonlyArray<Pick<ChecklistItemResponse, 'status'>>,
): string {
  if (items.length === 0) return 'No items yet.'
  const c = checklistCounts(items)
  const parts = [`${c.done} of ${c.total} done`]
  if (c.notApplicable > 0) parts.push(`${c.notApplicable} not applicable`)
  if (c.awaitingSignOff > 0) parts.push(`${c.awaitingSignOff} awaiting sign-off`)
  if (c.sentBack > 0) parts.push(`${c.sentBack} sent back`)
  return parts.join(', ')
}
